import {
  NetworkStatus,
  useMutation,
  useQuery,
  useSuspenseQuery,
} from "@apollo/client";
import {
  assetBalanceTransactionTypeToDisplayName,
  Button,
  ControlledDateFilterControl,
  ControlledDateFilterSummary,
  ControlledDecimalFilterControl,
  ControlledDecimalFilterSummary,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  ControlledStringArrayFilterControl,
  ControlledStringArrayFilterSummary,
  CurrencyAmountUI,
  cn,
  DataTable,
  DataTableColumnHeader,
  type DataTableToolbarProps,
  DataTableViewOptions,
  DateUI,
  DirectionIcon,
  externalAccountDetailsTypenameToDataSource,
  externalDataSourceToDisplayName,
  FXTradeCard,
  getAccountDisplayFields,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Number,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TransactionStatusUI,
  Txt,
  toast,
  transactionTypeToAssetBalanceTransactionType,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import {
  decimalSchema,
  downloadString,
  formatLocalDateComponents,
  neverFunc,
  prettyPrintEnum,
} from "@repo/utils";
import { createFileRoute, Link } from "@tanstack/react-router";
import type {
  ColumnDef,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import {
  createContext,
  Suspense,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { HiOutlineInformationCircle } from "react-icons/hi";
import { PiBuildingOffice } from "react-icons/pi";
import {
  RiBankLine,
  RiBuilding2Line,
  RiCalendarLine,
  RiDownload2Line,
  RiFilter3Line,
  RiFullscreenExitLine,
  RiFullscreenLine,
  RiNumbersLine,
  RiPlaneLine,
  RiPriceTagLine,
  RiShoppingBasket2Line,
  RiWalletLine,
} from "react-icons/ri";
import { useLocalStorage } from "usehooks-ts";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { FundMovementOptionsDialog } from "~/components/ledger-transaction-detail/FundMovementOptionsDialog";
import { graphql } from "~/gql/client";
import {
  assetKindValues,
  externalBankAccountDataSourceValues,
  ledgerEntryDirectionValues,
  transactionPageOrderValues,
  transactionStatusValues,
  transactionTypeValues,
} from "~/gql/client/enumValues";
import type {
  AdminTransactionRowFragment,
  TransactionPageFilter,
  TransactionPageOrder,
  TransactionStatus,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const externalDataSourceOptions = externalBankAccountDataSourceValues.map(
  (s) => ({ value: s, label: externalDataSourceToDisplayName[s] }),
);

const assetKindOptions = assetKindValues.map((k) => ({
  value: k,
  label: prettyPrintEnum(k),
}));

export const Route = createFileRoute("/__auth/transactions")({
  head: () => opsHead("Transactions"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const { useSearchParams } = useRouter();

  const [triggerCron] = useMutation(
    graphql(`
      mutation TransactionsPageTriggerCron($input: TriggerCronInput!) {
        triggerCron(input: $input) {
          jobId
          taskId
        }
      }
    `),
  );

  const handleTriggerCron = async (taskId: string) => {
    try {
      await triggerCron({ variables: { input: { taskId } } });
      toast({
        title: `${taskId} triggered`,
        kind: "success",
        autoHide: true,
      });
    } catch (error) {
      toast({
        kind: "error",
        autoHide: false,
        title: "Failed to trigger",
        description: String(error),
      });
    }
  };

  graphql(`
    fragment AdminTransactionRow on Transaction {
      id
      createdAt
      type
      direction
      currencyAmount {
        currency
        amount
      }
      effectiveAt
      postedAt
      status
      description
      reference
      tags {
        id
        key
        value
      }
      unitAmount {
        unit
        amount
      }
      ledgerAccount {
        ... on AssetLedgerAccount {
          asset {
            assetKind: kind
            ...AssetCardFields
            sourceDataAsOf
            lastSyncedAt
            businessEntity {
              id
              name
              organisation {
                id
                name
                squareLogoUrl
              }
            }
          }
        }
      }
      fundMovement {
        ... on Deposit {
          id
          destinationAsset {
            assetKind: kind
            ...AssetCardFields
            businessEntity {
              id
              name
              organisation {
                id
                name
                squareLogoUrl
              }
            }
          }
        }
        ... on Transfer {
          id
          sourceAsset {
            assetKind: kind
            ...AssetCardFields
          }
          destinationAccount {
            ...AssetCardFields
            ...BeneficiaryAssetCardFields
          }
          paymentReference
        }
        ... on EburyFXTrade {
          id
          ...FXTradeCardFields
        }
      }
    }
  `);

  const transactionsDocument = graphql(`
    query AdminTransactions($after: String, $first: Int!, $order: TransactionPageOrder, $filter: TransactionPageFilter) {
      transactions(after: $after, first: $first, order: $order, filter: $filter) {
        pageInfo {
          endCursor
          hasNextPage
        }
        transactions {
          ...AdminTransactionRow
        }
      }
    }
  `);

  const assetsDocument = graphql(`
    query AdminTransactionPage {
      custodyAccounts {
        id
        shortRef
        product {
          id
          internalName
        }
      }
      providers {
        id
        internalName
        name
      }
      products {
        id
        internalName
      }
      organisations {
        id
        name
        businessEntities {
          id
          name
          assets(includeInactive: true) {
            assetKind: kind
            id
            ...AssetCardFields
          }
        }
      }
    }
  `);

  const [filterAndSortState, setFilterAndSortOrder] =
    useQueryPersistedStateWithSchema({
      useSearchParams,
      schema: z.object({
        direction: z.enum(ledgerEntryDirectionValues).nullish(),
        assetIds: z.array(z.string()).optional().nullable(),
        endDateExclusive: z.date().optional().nullable(),
        assetKinds: z.array(z.enum(assetKindValues)).optional().nullable(),
        externalDataSources: z
          .array(z.enum(externalBankAccountDataSourceValues))
          .optional()
          .nullable(),
        organisationIds: z.array(z.string()).optional().nullable(),
        businessEntityIds: z.array(z.string()).optional().nullable(),
        custodyAccountIds: z.array(z.string()).optional().nullable(),
        productIds: z.array(z.string()).optional().nullable(),
        providerIds: z.array(z.string()).optional().nullable(),
        paymentReferences: z.array(z.string()).optional().nullable(),
        startDateInclusive: z.date().optional().nullable(),
        status: z.array(z.enum(transactionStatusValues)).optional().nullable(),
        transactionType: z
          .array(z.enum(transactionTypeValues))
          .optional()
          .nullable(),
        sortOrder: z.enum(transactionPageOrderValues).optional().nullable(),
        inflight: z.boolean().optional().nullable(),
        amount: z
          .object({
            gte: decimalSchema.optional().nullable(),
            lt: decimalSchema.optional().nullable(),
            eq: decimalSchema.optional().nullable(),
          })
          .optional()
          .nullable(),
      }),
      defaultState: {
        sortOrder: "EFFECTIVE_AT_DESC",
        assetKinds: ["PRIMARY_MANAGED"],
      },
    });

  const { sortOrder, ...filter } = filterAndSortState;

  const baseInput = {
    first: 60,
    order: sortOrder,
    filter,
  };

  const [requestTransactionExport, { loading: downloadCsvLoading }] =
    useMutation(
      graphql(`
        mutation AdminRequestTransactionExport($input: RequestTransactionExportInput!) {
          requestTransactionExport(input: $input) {
            status
            csvContents
            message
          }
        }
      `),
    );

  const downloadCsv = async () => {
    await requestTransactionExport({
      variables: {
        input: {
          filter: baseInput.filter ?? null,
          order: baseInput.order,
        },
      },
      onCompleted: (data) => {
        const result = data?.requestTransactionExport;
        if (result?.status === "READY" && result.csvContents) {
          downloadString(
            result.csvContents,
            `Primary transactions ${new Date().toISOString()}.csv`,
          );
        } else if (result?.status === "QUEUED") {
          toast({
            kind: "success",
            autoHide: false,
            title: "Your export is on its way",
            description: result.message ?? undefined,
          });
        }
      },
      onError: () => {
        toast({
          kind: "error",
          autoHide: false,
          title: "Something went wrong fetching transactions",
        });
      },
    });
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    transactionsDocument,
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const transactions = data?.transactions?.transactions ?? [];
  const nextCursor = data?.transactions?.pageInfo.hasNextPage
    ? (data.transactions.pageInfo.endCursor ?? undefined)
    : undefined;

  const { data: pageData } = useSuspenseQuery(assetsDocument);

  const handleSortChange = (newSortingState: SortingState) => {
    const primarySort = newSortingState.at(0);
    let newSortOrder: TransactionPageOrder;
    if (primarySort?.id === "date") {
      newSortOrder = primarySort.desc
        ? "EFFECTIVE_AT_DESC"
        : "EFFECTIVE_AT_ASC";
    } else if (primarySort?.id === "amount") {
      newSortOrder = primarySort.desc ? "AMOUNT_DESC" : "AMOUNT_ASC";
    } else {
      newSortOrder = "EFFECTIVE_AT_DESC";
    }
    if (newSortOrder !== sortOrder) {
      setFilterAndSortOrder({
        ...filterAndSortState,
        sortOrder: newSortOrder,
      });
    }
  };

  const productOptions =
    pageData.products?.flatMap((p) => ({
      value: p.id,
      label: p.internalName,
    })) ?? [];

  const providerOptions =
    pageData.providers?.flatMap((p) => ({
      value: p.id,
      searchValues: [p.name, p.internalName],
      label: (
        <div>
          {p.name}
          {p.internalName !== p.name ? ` (${p.internalName})` : ""}
        </div>
      ),
    })) ?? [];

  const custodyAccountOptions =
    pageData.custodyAccounts?.flatMap((custodyAccount) => ({
      value: custodyAccount.id,
      searchValues: [
        custodyAccount.shortRef,
        custodyAccount.product?.internalName,
      ].filter((item): item is string => !!item),
      label: custodyAccount.product?.internalName ?? "Unknown",
    })) ?? [];

  const assetOptions =
    pageData.organisations?.flatMap(
      (o) =>
        o.businessEntities?.flatMap(
          (be) =>
            be.assets?.map((a) => {
              const { businessEntityName, compactName } =
                getAccountDisplayFields({ account: a });
              return {
                value: a.id,
                searchValues: [a.name],
                label: `${compactName} (${businessEntityName})`,
                orgId: o.id,
              };
            }) ?? [],
        ) ?? [],
    ) ?? [];

  const organisationOptions =
    pageData.organisations?.flatMap((o) => ({
      value: o.id,
      label: o.name,
    })) ?? [];

  const businessEntityOptions = (pageData.organisations ?? [])
    .filter((org) => {
      if (filter.organisationIds && filter.organisationIds?.length > 0) {
        return filter.organisationIds.includes(org.id);
      }
      return true;
    })
    .flatMap((org) =>
      (org.businessEntities ?? []).map((be) => {
        return {
          value: be.id,
          searchValues: [be.name],
          label: (
            <div>
              {be.name} ({org.name})
            </div>
          ),
        };
      }),
    );

  const filterAndSortStateRef = useRef(filterAndSortState);
  filterAndSortStateRef.current = filterAndSortState;

  const [isFullWidth, setIsFullWidth] = useLocalStorage(
    "ops-transactions-full-width",
    false,
  );
  const [columnVisibility, setColumnVisibility] =
    useLocalStorage<VisibilityState>("ops-transactions-column-visibility", {});

  const [selectedTransactionId, setSelectedTransactionId] = useState<
    string | undefined
  >(undefined);

  const columns = useMemo<ColumnDef<AdminTransactionRowFragment>[]>(
    () => [
      {
        header: "ID",
        accessorKey: "id",
        enableHiding: false,
        cell: () => null,
      },
      {
        id: "status",
        accessorKey: "status",
        header: "",
        cell: ({ getValue }) => (
          <TransactionStatusUI
            iconOnly
            status={getValue<TransactionStatus>()}
          />
        ),
      },
      {
        id: "date",
        accessorFn: () => undefined,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Date" />
        ),
        cell: ({ row }) => {
          const { shortDate } = formatLocalDateComponents(
            row.original.effectiveAt,
          );

          return (
            <Tooltip>
              <TooltipTrigger>
                <div className="min-w-24">
                  <Txt>{shortDate}</Txt>
                </div>
              </TooltipTrigger>
              <TooltipContent className="border bg-white p-2">
                <Txt>
                  Effective:{" "}
                  <DateUI date={row.original.effectiveAt} variant="DATETIME" />
                </Txt>
                <Txt>
                  Posted:{" "}
                  {row.original.postedAt ? (
                    <DateUI date={row.original.postedAt} variant="DATETIME" />
                  ) : (
                    "-"
                  )}
                </Txt>
                <Txt>
                  Created:{" "}
                  <DateUI date={row.original.createdAt} variant="DATETIME" />
                </Txt>
                {row.original.tags && row.original.tags.length > 0 && (
                  <div className="mt-2 flex flex-col border-t pt-2">
                    {row.original.tags.map((tag) => (
                      <Txt key={tag.id} variant="caption">
                        {tag.key}
                        {tag.value ? `: ${tag.value}` : ""}
                      </Txt>
                    ))}
                  </div>
                )}
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: "effective_at",
        accessorFn: () => undefined,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Effective at" />
        ),
        cell: ({ row }) => {
          return (
            <div className="min-w-40">
              <DateUI date={row.original.effectiveAt} variant="DATETIME" />
            </div>
          );
        },
      },
      {
        id: "posted_at",
        accessorFn: () => undefined,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Posted at" />
        ),
        cell: ({ row }) => {
          return row.original.postedAt ? (
            <div className="min-w-40">
              <DateUI date={row.original.postedAt} variant="DATETIME" />
            </div>
          ) : (
            "-"
          );
        },
      },
      {
        id: "created_at",
        accessorFn: () => undefined,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="Created at" />
        ),
        cell: ({ row }) => {
          return (
            <div className="min-w-40">
              <DateUI date={row.original.createdAt} variant="DATETIME" />
            </div>
          );
        },
      },
      {
        id: "tags",
        accessorFn: () => undefined,
        header: "Tags",
        cell: ({ row }) => {
          const value = (row.original.tags ?? [])
            .map((tag) => `${tag.key}: ${tag.value}`)
            .join(", ");
          if (!value) {
            return null;
          }

          return (
            <Tooltip>
              <TooltipTrigger>
                <div className="max-w-[24rem]">
                  <Txt truncate>{value}</Txt>
                </div>
              </TooltipTrigger>
              <TooltipContent className="border bg-white p-2">
                <Txt>{value}</Txt>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: "business_entity",
        accessorFn: () => undefined,
        header: "Business Entity",
        cell: ({ row }) => {
          let beName: string | undefined = undefined;
          let beId: string | undefined = undefined;
          let orgName: string | undefined = undefined;
          let orgId: string | undefined = undefined;
          let logoUrl: string | undefined = undefined;

          if (
            row.original.ledgerAccount?.__typename === "AssetLedgerAccount" &&
            row.original.ledgerAccount.asset?.businessEntity
          ) {
            const be = row.original.ledgerAccount.asset.businessEntity;
            beName = be.name;
            beId = be.id;
            orgName = be.organisation?.name ?? "";
            orgId = be.organisation?.id;
            logoUrl = be.organisation?.squareLogoUrl ?? "";
          }
          return (
            <HoverCard openDelay={25}>
              <HoverCardTrigger asChild>
                <div
                  className={cn(
                    "flex cursor-default flex-row items-center gap-0",
                    !isFullWidth && "max-w-48",
                  )}
                >
                  {logoUrl ? (
                    <img
                      className="mr-2 size-6 shrink-0 rounded-sm"
                      src={logoUrl}
                      alt="Business entity logo"
                    />
                  ) : (
                    <PiBuildingOffice className="mr-2 shrink-0 text-sm text-tertiary" />
                  )}
                  <Txt truncate>{beName}</Txt>
                </div>
              </HoverCardTrigger>
              <HoverCardContent
                padding="sm"
                className="min-w-48 hover:cursor-default"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="mb-2 flex flex-row items-center gap-1 p-1">
                  {logoUrl ? (
                    <img
                      className="mr-2 size-6 shrink-0 rounded-sm"
                      src={logoUrl}
                      alt="Business entity logo"
                    />
                  ) : (
                    <PiBuildingOffice className="mr-2 shrink-0 text-sm text-tertiary" />
                  )}
                  <div className="flex min-w-0 flex-col">
                    <Txt>{beName}</Txt>
                    <Txt variant="caption">{orgName}</Txt>
                  </div>
                </div>
                {beId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-full justify-start rounded-xs text-xs"
                    onClick={() => {
                      if (!beId) {
                        return;
                      }
                      setFilterAndSortOrder({
                        ...filterAndSortStateRef.current,
                        businessEntityIds: [beId],
                        organisationIds: undefined,
                        assetIds: undefined,
                      });
                    }}
                  >
                    Filter by this entity
                  </Button>
                )}
                {orgId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-full justify-start rounded-xs text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if (!orgId) {
                        return;
                      }
                      setFilterAndSortOrder({
                        ...filterAndSortStateRef.current,
                        organisationIds: [orgId],
                        businessEntityIds: undefined,
                        assetIds: undefined,
                      });
                    }}
                  >
                    Filter by this organisation
                  </Button>
                )}

                {orgId && beId && (
                  <>
                    <Button
                      asChild
                      variant="ghost"
                      size="sm"
                      className="h-7 w-full justify-start rounded-xs text-xs"
                    >
                      <Link
                        to={`/organisations/${orgId}/business-entities/${beId}`}
                        target="_blank"
                      >
                        Go to entity
                      </Link>
                    </Button>
                    <Button
                      asChild
                      variant="ghost"
                      size="sm"
                      className="h-7 w-full justify-start rounded-xs text-xs"
                    >
                      <Link to={`/organisations/${orgId}`} target="_blank">
                        Go to organisation
                      </Link>
                    </Button>
                  </>
                )}
              </HoverCardContent>
            </HoverCard>
          );
        },
      },
      {
        id: "account",
        accessorFn: () => undefined,
        header: "Account",
        cell: ({ row }) => {
          const ledgerAccount = row.original.ledgerAccount;
          if (
            !ledgerAccount ||
            ledgerAccount.__typename !== "AssetLedgerAccount" ||
            !ledgerAccount.asset
          ) {
            return null;
          }
          const asset = ledgerAccount.asset;
          const {
            logoUrl,
            friendlyIdentifier,
            compactProviderName,
            compactName,
          } = getAccountDisplayFields({ account: asset });
          return (
            <HoverCard openDelay={25}>
              <HoverCardTrigger asChild>
                <div
                  className={cn(
                    "flex cursor-default items-center gap-1",
                    !isFullWidth && "max-w-[24rem]",
                  )}
                >
                  <img
                    src={logoUrl}
                    className="inline h-[1.5em] w-[1.5em] rounded-sm"
                  />
                  <div className={cn("text-left", !isFullWidth && "max-w-48")}>
                    <Txt truncate>{compactName}</Txt>
                  </div>
                </div>
              </HoverCardTrigger>
              <HoverCardContent
                padding="sm"
                className="min-w-48 hover:cursor-default"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="mb-2 flex items-center gap-1 p-1">
                  <img
                    src={logoUrl}
                    className="inline h-[1.5em] w-[1.5em] rounded-sm"
                  />
                  <div>
                    <Txt>
                      {compactProviderName} {compactName}
                    </Txt>
                    <Txt variant="caption">{friendlyIdentifier}</Txt>
                  </div>
                </div>
                {(asset.sourceDataAsOf || asset.lastSyncedAt) && (
                  <div className="mb-2 flex flex-col gap-2 px-1">
                    {asset.sourceDataAsOf && (
                      <Txt variant="caption">
                        Source data as of:{" "}
                        <DateUI
                          date={asset.sourceDataAsOf}
                          variant="DATETIME"
                        />
                      </Txt>
                    )}
                    {asset.lastSyncedAt && (
                      <Txt variant="caption">
                        Last synced:{" "}
                        <DateUI date={asset.lastSyncedAt} variant="DATETIME" />
                      </Txt>
                    )}
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-full justify-start rounded-xs text-xs"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setFilterAndSortOrder({
                      ...filterAndSortStateRef.current,
                      assetIds: [asset.id],
                      assetKinds: undefined,
                    });
                  }}
                >
                  Filter by this account
                </Button>

                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  className="h-7 w-full justify-start rounded-xs text-xs"
                >
                  <Link to={`/assets/${asset.id}`} target="_blank">
                    Go to account
                  </Link>
                </Button>
              </HoverCardContent>
            </HoverCard>
          );
        },
      },
      {
        id: "external_data_source",
        accessorFn: () => undefined,
        header: "External Data Source",
        cell: ({ row }) => {
          const ledgerAccount = row.original.ledgerAccount;
          if (
            ledgerAccount?.__typename !== "AssetLedgerAccount" ||
            ledgerAccount.asset?.__typename !== "BankAccountAsset" ||
            ledgerAccount.asset?.assetKind !== "CLIENT_MANAGED" ||
            !ledgerAccount.asset.details?.__typename
          ) {
            return null;
          }
          const externalDataSource =
            externalAccountDetailsTypenameToDataSource[
              ledgerAccount.asset.details.__typename
            ];
          return (
            <Txt>{externalDataSourceToDisplayName[externalDataSource]}</Txt>
          );
        },
      },
      {
        id: "description",
        accessorFn: () => undefined,
        header: "Description",
        cell: ({ row }) => {
          const value = row.original.description ?? row.original.reference;

          return (
            <Tooltip>
              <TooltipTrigger>
                <div className={cn(!isFullWidth && "max-w-64")}>
                  <Txt truncate>{value}</Txt>
                </div>
              </TooltipTrigger>
              <TooltipContent className="border bg-white p-2">
                <Txt truncate>{value}</Txt>
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: "amount",
        accessorFn: () => undefined,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title="Amount"
            align="right"
            className="flex flex-row justify-end pr-2"
          />
        ),
        cell: ({ row }) => {
          const { amount, currency } = row.original.currencyAmount;

          return (
            <div className="flex w-full flex-row justify-end gap-2">
              {row.original.unitAmount && (
                <Tooltip>
                  <TooltipTrigger>
                    <HiOutlineInformationCircle />
                  </TooltipTrigger>
                  <TooltipContent>
                    <Number
                      value={row.original.unitAmount.amount.mul(
                        row.original.direction === "DEBIT" ? -1 : 1,
                      )}
                      format={Number.format.units(
                        row.original.unitAmount.amount.mul(
                          row.original.direction === "DEBIT" ? -1 : 1,
                        ),
                      )}
                    />
                  </TooltipContent>
                </Tooltip>
              )}

              <span className="flex flex-row justify-end gap-1">
                <CurrencyAmountUI
                  amount={amount.mul(
                    row.original.direction === "DEBIT" ? -1 : 1,
                  )}
                  currency={currency}
                  showSymbol={false}
                />
                <DirectionIcon direction={row.original.direction} />
              </span>
            </div>
          );
        },
      },
      {
        id: "fund_movement",
        accessorFn: () => undefined,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            title="Fund movement"
            align="left"
            className="flex flex-row justify-end pr-2"
          />
        ),
        cell: ({ row }) => {
          switch (row.original.type) {
            case "TRANSFER_IN":
              if (
                row.original.fundMovement?.__typename === "Transfer" &&
                row.original.fundMovement.sourceAsset
              ) {
                const {
                  logoUrl,
                  compactProviderName,
                  compactName,
                  friendlyIdentifier,
                } = getAccountDisplayFields({
                  account: row.original.fundMovement.sourceAsset,
                });
                return (
                  <HoverCard openDelay={25}>
                    <HoverCardTrigger>
                      <Txt>
                        Transfer {row.original.fundMovement.paymentReference}
                      </Txt>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="flex flex-col gap-2 p-2 hover:cursor-default"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <Txt variant="caption">Transfer in from</Txt>
                      <div className="flex items-center gap-2">
                        <img
                          src={logoUrl}
                          className="inline h-[1.5em] w-[1.5em] rounded-sm"
                        />
                        <div>
                          <Txt>
                            {compactProviderName} {compactName}
                          </Txt>
                          <Txt variant="mono">{friendlyIdentifier}</Txt>
                        </div>
                      </div>

                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="h-7 w-full justify-start rounded-xs text-xs"
                      >
                        <Link
                          to="/transfers/$id"
                          params={{ id: row.original.fundMovement.id }}
                          target="_blank"
                        >
                          View transfer
                        </Link>
                      </Button>
                    </HoverCardContent>
                  </HoverCard>
                );
              }
              return "Transfer In";
            case "TRANSFER_OUT":
              if (
                row.original.fundMovement?.__typename === "Transfer" &&
                row.original.fundMovement.destinationAccount
              ) {
                const {
                  logoUrl,
                  compactProviderName,
                  compactName,
                  friendlyIdentifier,
                } = getAccountDisplayFields({
                  account: row.original.fundMovement.destinationAccount,
                });

                const actionLabel =
                  row.original.fundMovement.destinationAccount.__typename ===
                  "BeneficiaryAccount"
                    ? "Payout"
                    : "Transfer";

                return (
                  <HoverCard openDelay={25}>
                    <HoverCardTrigger>
                      <Txt>
                        {actionLabel}{" "}
                        {row.original.fundMovement.paymentReference}
                      </Txt>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="flex flex-col gap-2 p-2 hover:cursor-default"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <Txt variant="caption">{actionLabel} to</Txt>
                      <div className="flex items-center gap-2">
                        <img
                          src={logoUrl}
                          className="inline h-[1.5em] w-[1.5em] rounded-sm"
                        />
                        <div>
                          <Txt>
                            {compactProviderName} {compactName}
                          </Txt>
                          <Txt variant="mono">{friendlyIdentifier}</Txt>
                        </div>
                      </div>

                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="h-7 w-full justify-start rounded-xs text-xs"
                      >
                        <Link
                          to="/transfers/$id"
                          params={{ id: row.original.fundMovement.id }}
                          target="_blank"
                        >
                          View transfer
                        </Link>
                      </Button>
                    </HoverCardContent>
                  </HoverCard>
                );
              }
              return "Transfer out";
            case "MARGIN":
              const ledgerAccount = row.original.ledgerAccount;
              if (
                row.original.fundMovement?.__typename === "EburyFXTrade" &&
                ledgerAccount &&
                ledgerAccount.__typename === "AssetLedgerAccount" &&
                ledgerAccount.asset
              ) {
                return (
                  <HoverCard openDelay={25}>
                    <HoverCardTrigger>
                      <Txt>FX Trade (margin)</Txt>
                    </HoverCardTrigger>
                    <HoverCardContent
                      className="flex flex-col gap-2 p-2 hover:cursor-default"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                    >
                      <FXTradeCard fxTrade={row.original.fundMovement} />
                      <Button
                        asChild
                        variant="ghost"
                        size="sm"
                        className="h-7 w-full justify-start rounded-xs text-xs"
                      >
                        <Link
                          to="/fx-trades/$id"
                          params={{ id: row.original.fundMovement.id }}
                          target="_blank"
                        >
                          View FX trade
                        </Link>
                      </Button>
                    </HoverCardContent>
                  </HoverCard>
                );
              }
              return "FX Margin";
            case "UNCATEGORISED":
              return (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setSelectedTransactionId(row.original.id);
                  }}
                >
                  Categorise
                </Button>
              );
            case "BALANCE_SYNC":
            case "PROVIDER_FEE":
            case "CUSTODY_FEE_SWEEP":
            case "STATEMENT_FEE":
            case "STRIPE_PAYOUT":
            case "YIELD_REMITTANCE":
            case "DIRECT_WITHDRAWAL":
            case "YIELD_REINVESTMENT":
            case "DEPOSIT":
            case "TAX":
            case "YIELD":
            case "FUND_TRADE":
            case "PAYOUT":
              return prettyPrintEnum(row.original.type);
            case null:
            case undefined:
              return null;
            default:
              neverFunc(row.original.type);
          }
        },
      },
    ],
    [setFilterAndSortOrder, isFullWidth],
  );

  return (
    <AdminPageContainer testId="dashboard-transactions" fullWidth={isFullWidth}>
      <AdminPageHeader
        title="Transactions"
        subtitle={"Changes to your account balances"}
        buttons={
          <div className="flex flex-row gap-4">
            <Link to="/transactions/batch-import">
              <Button>Batch import</Button>
            </Link>
            <Button
              variant="outline"
              onClick={() =>
                void handleTriggerCron("initiateMacquarieAccountsSyncCronTask")
              }
            >
              Sync Macquarie
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                void handleTriggerCron(
                  "initiateReconcileMacquarieTransactionsCronTask",
                )
              }
            >
              Rec Macquarie
            </Button>
            <Link to="/transactions/new">
              <Button variant="primary">New Transaction</Button>
            </Link>
          </div>
        }
      />
      <TransactionsTableToolbarContext.Provider
        value={{
          isFullWidth,
          onToggleFullWidth: () => setIsFullWidth((prev) => !prev),
          filterBarProps: {
            filterValue: filter,
            onFilterChange: (values) => {
              setFilterAndSortOrder({
                ...values,
                sortOrder,
              });
            },
            assetOptions,
            productOptions,
            organisationOptions,
            businessEntityOptions,
            providerOptions,
            custodyAccountOptions,
          },
        }}
      >
        <DataTable
          data={transactions}
          columns={columns}
          manualSorting
          manualFiltering
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          manualPagination
          maxMultiSortColCount={1}
          hidePagination
          pageSize={10_000}
          className="overflow-x-auto border"
          cellClassName="p-2"
          linkTo={(row) => {
            return `/transactions/${row.original.id}`;
          }}
          onSortingChange={handleSortChange}
          columnSortProps={{
            id: "date",
            desc: true,
          }}
          columnVisibility={{
            effective_at: false,
            posted_at: false,
            created_at: false,
            tags: false,
            external_data_source: false,
            ...columnVisibility,
            id: false,
          }}
          onColumnVisibilityChange={setColumnVisibility}
          toolbarComponent={TransactionsTableToolbar}
          showCsvExport={false}
        />
      </TransactionsTableToolbarContext.Provider>
      <FundMovementOptionsDialog
        transactionId={selectedTransactionId}
        onClose={() => setSelectedTransactionId(undefined)}
      />
      <div className="mt-4 flex w-full flex-row items-center justify-center gap-4">
        {nextCursor ? (
          <Button
            onClick={() =>
              fetchMore({
                variables: {
                  after: nextCursor,
                  ...baseInput,
                },
              })
            }
            variant="outline"
            loading={loading || networkStatus === NetworkStatus.fetchMore}
          >
            Load more
          </Button>
        ) : (
          !!transactions.length && (
            <p className="text-secondary text-sm">No more data</p>
          )
        )}
        {!!transactions.length && (
          <Button
            variant="outline"
            leftIcon={<RiDownload2Line />}
            onClick={() => void downloadCsv()}
            disabled={downloadCsvLoading}
            loading={downloadCsvLoading}
          >
            Export
          </Button>
        )}
      </div>
    </AdminPageContainer>
  );
}

// DataTable renders toolbarComponent as a component, so it must be a stable
// module-level reference; an inline closure would remount FilterBar (closing
// its popover) on every filter change. Props are passed via context instead.
const TransactionsTableToolbarContext = createContext<
  | {
      filterBarProps: FilterBarProps;
      isFullWidth: boolean;
      onToggleFullWidth: () => void;
    }
  | undefined
>(undefined);

function TransactionsTableToolbar({
  table,
}: DataTableToolbarProps<AdminTransactionRowFragment>) {
  const toolbarContext = useContext(TransactionsTableToolbarContext);
  if (!toolbarContext) {
    throw new Error(
      "TransactionsTableToolbar requires TransactionsTableToolbarContext",
    );
  }
  const { filterBarProps, isFullWidth, onToggleFullWidth } = toolbarContext;
  return (
    <div className="flex flex-row items-start justify-between gap-2">
      <FilterBar {...filterBarProps} />
      <div className="flex flex-row gap-2">
        <DataTableViewOptions table={table} />
        <Button
          variant="outline"
          size="sm"
          className="h-8"
          leftIcon={
            isFullWidth ? <RiFullscreenExitLine /> : <RiFullscreenLine />
          }
          onClick={onToggleFullWidth}
        >
          {isFullWidth ? "Exit fullscreen" : "Fullscreen"}
        </Button>
      </div>
    </div>
  );
}

type FilterBarProps = {
  filterValue: TransactionPageFilter;
  onFilterChange: (_: TransactionPageFilter) => void;
  assetOptions: {
    value: string;
    label: ReactNode;
    orgId: string;
  }[];
  organisationOptions: {
    value: string;
    label: ReactNode;
  }[];
  businessEntityOptions: {
    value: string;
    label: ReactNode;
  }[];
  productOptions: {
    value: string;
    label: ReactNode;
  }[];
  providerOptions: {
    value: string;
    label: ReactNode;
  }[];
  custodyAccountOptions: {
    value: string;
    label: ReactNode;
    searchValues?: string[];
  }[];
};

function FilterBar({
  filterValue,
  onFilterChange,
  assetOptions,
  organisationOptions,
  businessEntityOptions,
  productOptions,
  providerOptions,
  custodyAccountOptions,
}: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("date");
  const filterCount =
    0 +
    (filterValue.direction ? 1 : 0) +
    ((filterValue?.status?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.assetIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.organisationIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.businessEntityIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.productIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.transactionType?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.providerIds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.paymentReferences?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.custodyAccountIds?.length ?? 0) > 0 ? 1 : 0) +
    (filterValue.startDateInclusive || filterValue.endDateExclusive ? 1 : 0) +
    (filterValue.amount?.gte || filterValue.amount?.lt || filterValue.amount?.eq
      ? 1
      : 0) +
    ((filterValue?.assetKinds?.length ?? 0) > 0 ? 1 : 0) +
    ((filterValue?.externalDataSources?.length ?? 0) > 0 ? 1 : 0) +
    (filterValue.inflight ? 1 : 0);

  const resetFilter = () => {
    setPath("date");
    onFilterChange({
      ...filterValue,
      direction: undefined,
      startDateInclusive: undefined,
      endDateExclusive: undefined,
      organisationIds: undefined,
      businessEntityIds: undefined,
      status: undefined,
      assetIds: undefined,
      productIds: undefined,
      providerIds: undefined,
      paymentReferences: undefined,
      custodyAccountIds: undefined,
      transactionType: undefined,
      amount: undefined,
      inflight: undefined,
      assetKinds: undefined,
      externalDataSources: undefined,
    });
  };

  const statusOptions = [
    {
      value: "POSTED" as const,
      label: <span>Posted</span>,
    },
    {
      value: "PENDING" as const,
      label: <span>Pending</span>,
    },
  ];

  const directionOptions = [
    { value: "DEBIT" as const, label: "Debit" },
    { value: "CREDIT" as const, label: "Credit" },
  ];

  const typeOptions = transactionTypeValues.map((k) => ({
    value: k,
    label: (
      <span>
        {
          assetBalanceTransactionTypeToDisplayName[
            transactionTypeToAssetBalanceTransactionType[k]
          ]
        }
      </span>
    ),
  }));

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(open) => setOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0" align="start">
          <div className="flex w-[260px] flex-col justify-between overflow-y-auto rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("date")}
              >
                <RiCalendarLine />
                <div className="flex flex-1">Date</div>
                {(filterValue.endDateExclusive ||
                  filterValue.startDateInclusive) && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("status")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Status</div>
                {(filterValue.status?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("org")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("business_entity")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Business Entities</div>
                {(filterValue.businessEntityIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("account")}
              >
                <RiWalletLine />
                <div className="flex flex-1">Account</div>
                {(filterValue.assetIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("product")}
              >
                <RiShoppingBasket2Line />
                <div className="flex flex-1">Product</div>
                {(filterValue.productIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("provider")}
              >
                <RiBankLine />
                <div className="flex flex-1">Provider</div>
                {(filterValue.providerIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("custody_account")}
              >
                <RiWalletLine />
                <div className="flex flex-1">Custody Account</div>
                {(filterValue.custodyAccountIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("direction")}
              >
                <RiNumbersLine />
                <div className="flex flex-1">Debit/Credit</div>
                {filterValue.direction && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("type")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Type</div>
                {(filterValue.transactionType?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("payment_reference")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Reference</div>
                {(filterValue.paymentReferences?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("amount")}
              >
                <RiNumbersLine />
                <div className="flex flex-1">Amount</div>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("inflight")}
              >
                <RiPlaneLine />
                <div className="flex flex-1">Inflight</div>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("assetKind")}
              >
                <RiWalletLine />
                <div className="flex flex-1">Account kind</div>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("externalDataSource")}
              >
                <RiBankLine />
                <div className="flex flex-1">External data source</div>
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "payment_reference" && (
              <ControlledStringArrayFilterControl
                filterValue={filterValue.paymentReferences ?? undefined}
                onChange={(paymentReferences) => {
                  onFilterChange({ ...filterValue, paymentReferences });
                }}
              />
            )}
            {path === "date" && (
              <ControlledDateFilterControl
                onChange={(value) => {
                  onFilterChange({
                    ...filterValue,
                    startDateInclusive: value?.startAtInclusive,
                    endDateExclusive: value?.endAtExclusive,
                  });
                }}
                filterValue={{
                  startAtInclusive: filterValue.startDateInclusive ?? undefined,
                  endAtExclusive: filterValue.endDateExclusive ?? undefined,
                }}
              />
            )}
            {path === "status" && (
              <ControlledFacetedFilterControl
                options={statusOptions}
                selectedValues={filterValue.status ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    status: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "direction" && (
              <ControlledFacetedFilterControl
                options={directionOptions}
                selectedValues={
                  filterValue.direction ? [filterValue.direction] : []
                }
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    direction: newSelectedValues[0],
                  });
                }}
              />
            )}
            {path === "type" && (
              <ControlledFacetedFilterControl
                options={typeOptions}
                selectedValues={filterValue.transactionType ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    transactionType: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "amount" && (
              <ControlledDecimalFilterControl
                onChange={(value) => {
                  onFilterChange({
                    ...filterValue,
                    amount: value,
                  });
                }}
                filterValue={{
                  gte: filterValue.amount?.gte ?? undefined,
                  lt: filterValue.amount?.lt ?? undefined,
                  eq: filterValue.amount?.eq ?? undefined,
                }}
              />
            )}
            {path === "inflight" && (
              <ControlledFacetedFilterControl
                options={[{ value: true, label: "Yes" }]}
                selectedValues={filterValue.inflight ? [true] : []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    inflight: newSelectedValues[0],
                  });
                }}
              />
            )}
            {path === "assetKind" && (
              <ControlledFacetedFilterControl
                options={assetKindOptions}
                selectedValues={filterValue.assetKinds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    assetKinds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "externalDataSource" && (
              <ControlledFacetedFilterControl
                options={externalDataSourceOptions}
                selectedValues={filterValue.externalDataSources ?? []}
                onChange={(newSelectedValues) => {
                  // Data sources only exist on client managed assets, so make sure they aren't filtered out by kind.
                  const assetKinds =
                    newSelectedValues.length &&
                    filterValue.assetKinds?.length &&
                    !filterValue.assetKinds.includes("CLIENT_MANAGED")
                      ? [...filterValue.assetKinds, "CLIENT_MANAGED" as const]
                      : filterValue.assetKinds;
                  onFilterChange({
                    ...filterValue,
                    assetKinds,
                    externalDataSources: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "account" && (
              <ControlledFacetedFilterControl
                options={
                  filterValue.organisationIds &&
                  filterValue.organisationIds.length
                    ? assetOptions.filter((a) =>
                        filterValue.organisationIds?.includes(a.orgId),
                      )
                    : assetOptions
                }
                selectedValues={filterValue.assetIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    assetIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "product" && (
              <ControlledFacetedFilterControl
                options={productOptions}
                selectedValues={filterValue.productIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    productIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "provider" && (
              <ControlledFacetedFilterControl
                options={providerOptions}
                selectedValues={filterValue.providerIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    providerIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "custody_account" && (
              <ControlledFacetedFilterControl
                options={custodyAccountOptions}
                selectedValues={filterValue.custodyAccountIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    custodyAccountIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "org" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                    // business entity options are narrowed depends on the org - reset when changing org
                    businessEntityIds: undefined,
                  });
                }}
              />
            )}
            {path === "business_entity" && (
              <ControlledFacetedFilterControl
                options={businessEntityOptions}
                selectedValues={filterValue.businessEntityIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    businessEntityIds: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledDateFilterSummary
        onClick={() => {
          setPath("date");
          setOpen(true);
        }}
        value={{
          startAtInclusive: filterValue.startDateInclusive ?? undefined,
          endAtExclusive: filterValue.endDateExclusive ?? undefined,
        }}
      />

      <ControlledFactedFilterSummary
        title="Status"
        options={statusOptions}
        selectedValues={filterValue.status ?? []}
        onClick={() => {
          setPath("status");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Debit/Credit"
        options={directionOptions}
        selectedValues={filterValue.direction ? [filterValue.direction] : []}
        onClick={() => {
          setPath("direction");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Type"
        options={typeOptions}
        selectedValues={filterValue.transactionType ?? []}
        onClick={() => {
          setPath("type");
          setOpen(true);
        }}
      />

      <ControlledStringArrayFilterSummary
        title="Reference"
        value={filterValue.paymentReferences ?? undefined}
        onClick={() => {
          setPath("payment_reference");
          setOpen(true);
        }}
      />

      <ControlledDecimalFilterSummary
        title="Amount"
        onClick={() => {
          setPath("amount");
          setOpen(true);
        }}
        value={{
          gte: filterValue.amount?.gte ?? undefined,
          lt: filterValue.amount?.lt ?? undefined,
          eq: filterValue.amount?.eq ?? undefined,
        }}
      />

      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("org");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Business Entity"
        options={businessEntityOptions}
        selectedValues={filterValue.businessEntityIds ?? []}
        onClick={() => {
          setPath("business_entity");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Product"
        options={productOptions}
        selectedValues={filterValue.productIds ?? []}
        onClick={() => {
          setPath("product");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Provider"
        options={providerOptions}
        selectedValues={filterValue.providerIds ?? []}
        onClick={() => {
          setPath("provider");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Custody Account"
        options={custodyAccountOptions}
        selectedValues={filterValue.custodyAccountIds ?? []}
        onClick={() => {
          setPath("custody_account");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Account"
        options={assetOptions}
        selectedValues={filterValue.assetIds ?? []}
        onClick={() => {
          setPath("account");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="In flight"
        options={[{ value: "Yes", label: "Yes" }]}
        selectedValues={filterValue.inflight ? ["Yes"] : []}
        onClick={() => {
          setPath("inflight");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Account kind"
        options={assetKindOptions}
        selectedValues={filterValue.assetKinds ?? []}
        onClick={() => {
          setPath("assetKind");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="External Data Source"
        options={externalDataSourceOptions}
        selectedValues={filterValue.externalDataSources ?? []}
        onClick={() => {
          setPath("externalDataSource");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
